import React, { useState, forwardRef, useImperativeHandle, useEffect } from "react";
import PropTypes from 'prop-types';
import dayjs from 'dayjs';
import { useTranslation } from 'react-i18next';
import { Form, DatePicker, Select, AutoComplete, Space, Typography } from 'antd';
import useDropdownItem from '../../hooks/useDropdownItem';
import useSearchItem from '../../hooks/useSeatchItem';
import { URL_SCALE_LARGE } from '../../constance/urls';
import { HUMAN_DATE_FORMAT, DATE_FORMAT } from "../../constance/strings";
import { useDebounce } from 'use-debounce';

const { RangePicker } = DatePicker;

const ReportLogWoodFilters = forwardRef((props, ref) => {
  const { t } = useTranslation();
  const [form] = Form.useForm();
  const date = Form.useWatch('dateRange', form);
  const searchText = Form.useWatch("truckCompanyCode", form);
  const [debouncedText] = useDebounce(searchText, 500);
  const transactionType = Form.useWatch("transactionTypeId", form)
  const tableRef = React.useRef();
  const [truckCompanies, setTruckCompanies] = useState([]);
  const [transactionTypes, setTransactionTypes] = useState([]);

  const truckCompanySearch = useSearchItem({
    url: URL_SCALE_LARGE.TRUCK_COMPANY,
    description_render: 'name',
    title_render: 'code',
  });

  const transactionTypeDropdown = useDropdownItem({
    url: URL_SCALE_LARGE.TRANSACTION_TYPE,
  });

  React.useEffect(() => {
    if (tableRef.current && (debouncedText.length >= 2 || date || transactionType)) {
      const formattedDate = date ? dayjs(date).format(DATE_FORMAT) : null;
      tableRef.current.fetch({
        date: formattedDate,
        transactionType: transactionType,
        searchText: debouncedText,
      });
    }
    const queryString = ref?.current?.getParams();
    if (queryString) {
      props.onFiltered(queryString);
    }
  }, [date, transactionType, debouncedText])

  useEffect(() => {
    setTruckCompanies(truckCompanySearch.response || []);
    setTransactionTypes(transactionTypeDropdown.response || []);
  }, [truckCompanySearch.response, transactionTypeDropdown.response]);

  useImperativeHandle(ref, () => ({
    getParams: () => {
      const values = form.getFieldsValue();
      if (!values.dateRange || values.dateRange.length < 2) return '';
      const startDate = dayjs(values.dateRange[0]).format(DATE_FORMAT);
      const endDate = dayjs(values.dateRange[1]).format(DATE_FORMAT);
      let queryString = `date_after=${startDate}&date_before=${endDate}`;
      if (values.truckCompanyCode) {
        queryString += `&truck_company_code=${values.truckCompanyCode}&truck_company_name=${values.truckCompanyName}`;
      }
      if (values.transactionTypeId) {
        queryString += `&transaction_type_id=${values.transactionTypeId}`;
      }
      return queryString;
    },
  }));


  return (
    <Form
      form={form}
      layout="inline"
      initialValues={{
        dateRange: [],
        truckCompany: '',
        truckCompanyCode: null,
        transactionTypeId: null,
      }}
      style={{ marginTop: 24, marginBottom: 24 }}
    >
      <Form.Item
        name="dateRange"
        label={t('filter')}
        rules={[{ required: true, message: t('กรุณาเลือกช่วงเวลา') }]}
      >
        <RangePicker
          format={HUMAN_DATE_FORMAT}
          placeholder={[
            t('searchs.placeholders.startDate'),
            t('searchs.placeholders.endDate'),
          ]}
        />
      </Form.Item>

      {!props.hideCustomerFilter && (
        <>
          <Form.Item name="truckCompanyCode" hidden={true} />
          <Form.Item name="truckCompany" label={t("truck_transactions.company")}>
            <AutoComplete
              style={{ width: 200 }}
              allowClear
              placeholder={`${t("search")} ${t("truck_transactions.company")}`}
              options={truckCompanies.map(company => ({
                ...company,
                value: company.name,
                label: (
                  <Space direction="vertical" size={1}>
                    <Typography.Text strong>{company.code}</Typography.Text>
                    <Typography.Text type="secondary" >{company.name}</Typography.Text>
                  </Space>
                )
              }))}
              onSearch={(value) => {
                truckCompanySearch.fetchData({ search: value });
              }}
              onSelect={(value, option) => {
                form.setFieldValue("truckCompanyCode", option.code);
              }}
              onClear={() => {
                form.setFieldValue("truckCompany", "");
                form.setFieldValue("truckCompanyCode", "");
              }}
              notFoundContent={searchText ? t('no_data') : ''}
            />
          </Form.Item>
        </>
      )}

      <Form.Item name="transactionTypeId" label={t('source')}>
        <Select
          style={{ width: 200 }}
          placeholder={t('source_filter')}
          allowClear
          options={transactionTypes.map((item) => ({
            label: item.name,
            value: item.id,
          }))}
        />
      </Form.Item>
    </Form>
  );
});

export default ReportLogWoodFilters;

ReportLogWoodFilters.propTypes = {
  onFiltered: PropTypes.func,
  style: PropTypes.object,
  hideCustomerFilter: PropTypes.bool,
};

ReportLogWoodFilters.defaultProps = {
  onFiltered: () => { },
  style: {},
  hideCustomerFilter: false,
};
